import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { cn, complianceStatusTone, ensLevelTone, humanCategory, scoreTone } from "@/lib/util";
import { ArrowLeft, ScrollText, ShieldCheck, ChevronDown, ChevronRight, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { ENSLevelSelector } from "@/components/security/ENSLevelSelector";

const ENS_ID = "ens_rd311_2022";

export default function Compliance() {
  const { id } = useParams();
  const [summary, setSummary] = useState(null);
  const [framework, setFramework] = useState(null); // full detail of selected framework
  const [selected, setSelected] = useState(null);   // framework_id string
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/clients/${id}/compliance`);
      setSummary(r.data);
      const first = r.data.frameworks[0]?.framework?.id;
      if (first) setSelected(first);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load compliance");
    } finally { setLoading(false); }
  }, [id]);

  const loadFramework = useCallback(async (fwId) => {
    if (!fwId) return;
    try {
      const r = await api.get(`/clients/${id}/compliance/${fwId}`);
      setFramework(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load framework detail");
    }
  }, [id]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { loadFramework(selected); }, [selected, loadFramework]);

  if (loading || !summary) {
    return <div className="p-8 font-mono text-sm text-muted-foreground inline-flex items-center gap-2">
      <Loader2 className="w-3 h-3 animate-spin" /> Construyendo registro de cumplimiento…
    </div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="compliance-page">
      <Link to={`/clients/${id}`} className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-3 h-3" /> Volver al cliente
      </Link>

      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2 inline-flex items-center gap-2">
            <ScrollText className="w-3 h-3 text-primary" /> Registro de cumplimiento
          </div>
          <h1 className="font-display font-bold tracking-tight" style={{ fontSize: "clamp(32px, 4vw, 48px)" }}>
            {summary.client_name}
          </h1>
          <p className="font-mono text-xs text-muted-foreground mt-2 max-w-xl">
            Hallazgos EASM mapeados a tus marcos de gobierno. Cada control muestra la peor
            severidad observada y los hallazgos de muestra que contribuyen a ella.
          </p>
        </div>
      </header>

      {/* Framework cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="framework-cards">
        {summary.frameworks.map((f) => {
          const s = f.summary;
          const tone = scoreTone(s.score);
          return (
            <button
              key={f.framework.id}
              onClick={() => setSelected(f.framework.id)}
              className={cn(
                "text-left border p-5 transition-colors",
                selected === f.framework.id
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-muted-foreground bg-card/50"
              )}
              data-testid={`framework-card-${f.framework.id}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="overline">{f.framework.short}</div>
                  <div className="font-display text-lg font-medium mt-1">{f.framework.name}</div>
                  <div className="font-mono text-[10px] text-muted-foreground mt-1">v{f.framework.version}</div>
                </div>
                <div className={cn("font-display font-bold text-3xl", tone.text)}>{s.score}</div>
              </div>
              <div className="mt-4 h-1 bg-muted overflow-hidden">
                <div className="h-full" style={{ width: `${s.score}%`, backgroundColor: tone.hex }} />
              </div>
              <div className="mt-4 grid grid-cols-5 gap-1 font-mono text-[10px] uppercase tracking-widest">
                <StatusPill label="Crítico" count={s.critical} status="critical" />
                <StatusPill label="Alto"     count={s.high}     status="high" />
                <StatusPill label="Atn"     count={s.attention} status="attention" />
                <StatusPill label="Vigilar"    count={s.watch}    status="watch" />
                <StatusPill label="OK"    count={s.clean}    status="clean" />
              </div>
              <div className="mt-2 font-mono text-[10px] text-muted-foreground">
                {s.controls_with_evidence} / {s.total_controls} controles con evidencia EASM
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected framework detail */}
      {framework && (
        <section className="space-y-4" data-testid="framework-detail">
          <div className="flex items-center justify-between border-b border-border pb-2">
            <div>
              <div className="overline">{framework.framework.short} · {framework.framework.version}</div>
              <h2 className="font-display text-2xl font-medium mt-1">Registro de controles</h2>
            </div>
            <ExportControls framework={framework} />
          </div>

          {framework.framework.id === ENS_ID && (
            <ENSLevelSelector
              clientId={id}
              level={framework.ens_level || summary.ens_level}
              appliedCount={framework.summary.total_controls}
              totalCount={26}
              onChanged={() => {
                // Refetch both the ENS detail and the summary so scores/counters update
                loadFramework(ENS_ID);
                loadSummary();
              }}
            />
          )}

          {framework.themes.map((theme) => (
            <ThemeBlock
              key={theme.id}
              theme={theme}
              controls={framework.controls.filter((c) => c.theme_id === theme.id)}
              expanded={expanded}
              onToggle={(cid) => setExpanded((e) => ({ ...e, [cid]: !e[cid] }))}
              clientId={id}
            />
          ))}
        </section>
      )}
    </div>
  );
}

function StatusPill({ label, count, status }) {
  const tone = complianceStatusTone(status);
  return (
    <div className={cn("border px-1.5 py-1 flex flex-col items-center", tone.border, tone.bg)}>
      <span className={cn("font-bold text-sm", tone.text)}>{count}</span>
      <span className="text-muted-foreground">{label}</span>
    </div>
  );
}

function ThemeBlock({ theme, controls, expanded, onToggle, clientId }) {
  return (
    <div className="border border-border" data-testid={`theme-${theme.id}`}>
      <div className="border-b border-border p-3 bg-card/50 flex items-center justify-between">
        <div className="font-mono text-[10px] tracking-widest uppercase text-muted-foreground">
          {theme.name}
        </div>
        <div className="flex gap-2 font-mono text-[10px]">
          <span className="text-muted-foreground">{theme.total} controles</span>
          {theme.critical > 0 && <span className="text-sev-critical">· {theme.critical} críticos</span>}
          {theme.high > 0 && <span className="text-sev-high">· {theme.high} altos</span>}
        </div>
      </div>
      <div>
        {controls.map((c) => (
          <ControlRow
            key={c.id}
            control={c}
            expanded={!!expanded[c.id]}
            onToggle={() => onToggle(c.id)}
            clientId={clientId}
          />
        ))}
      </div>
    </div>
  );
}

function ControlRow({ control, expanded, onToggle, clientId }) {
  const tone = complianceStatusTone(control.status);
  const Chev = expanded ? ChevronDown : ChevronRight;
  return (
    <div className="border-b border-border/50 last:border-b-0" data-testid={`control-${control.id}`}>
      <button
        onClick={onToggle}
        className="w-full text-left p-3 hover:bg-muted/30 transition-colors flex items-center gap-3"
      >
        <Chev className="w-3 h-3 text-muted-foreground flex-shrink-0" />
        <div className="font-mono text-xs text-primary w-20 flex-shrink-0">{control.id}</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm truncate">{control.name}</div>
          <div className="font-mono text-[10px] text-muted-foreground truncate">{control.description}</div>
        </div>
        <span className={cn("font-mono text-[10px] tracking-widest uppercase border px-2 py-0.5",
                            tone.text, tone.border, tone.bg)}>
          {tone.label}
        </span>
        <span className="font-mono text-[10px] text-muted-foreground w-16 text-right">
          {control.finding_count > 0 ? `${control.finding_count} hallazgo${control.finding_count === 1 ? "" : "s"}` : "—"}
        </span>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pl-16 space-y-2 bg-background/50">
          {control.related_categories.length > 0 && (
            <div className="text-[11px] text-muted-foreground">
              <span className="overline mr-2">Mapeado desde</span>
              {control.related_categories.map((cat) => (
                <span key={cat} className="font-mono text-[10px] border border-border px-1.5 py-0.5 mr-1">
                  {humanCategory(cat)}
                </span>
              ))}
            </div>
          )}
          {control.sample_findings.length > 0 ? (
            <div className="space-y-1">
              {control.sample_findings.map((f) => (
                <Link
                  key={f.id}
                  to={`/findings/${f.id}`}
                  className="flex items-center gap-3 border border-border/50 hover:border-primary hover:bg-primary/5 p-2 transition-colors"
                >
                  <span className={cn("font-mono text-[10px] tracking-widest uppercase",
                                       complianceStatusTone(f.severity === "critical" ? "critical"
                                         : f.severity === "high" ? "high"
                                         : f.severity === "medium" ? "attention" : "watch").text)}>
                    {f.severity}
                  </span>
                  <span className="text-xs flex-1 min-w-0 truncate">{f.title}</span>
                  {f.cve && <span className="font-mono text-[10px] text-muted-foreground">{f.cve}</span>}
                  <span className="font-mono text-[10px] text-muted-foreground">risk {f.risk_score}</span>
                  <ExternalLink className="w-3 h-3 text-muted-foreground" />
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-[11px] font-mono text-muted-foreground inline-flex items-center gap-1">
              <ShieldCheck className="w-3 h-3 text-sev-low" /> Sin hallazgos abiertos mapeados a este control.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ExportControls({ framework }) {
  const isEns = framework.framework.id === ENS_ID;
  const downloadCsv = () => {
    const baseHeader = ["control_id", "name", "theme", "status", "worst_severity", "finding_count", "description"];
    const header = (isEns ? [...baseHeader, "ens_level"] : baseHeader).join(",");
    const rows = framework.controls.map((c) => {
      const base = [c.id, csv(c.name), csv(c.theme), c.status, c.worst_severity || "", c.finding_count, csv(c.description)];
      return (isEns ? [...base, c.level || ""] : base).join(",");
    });
    const preamble = [];
    if (isEns) {
      const now = new Date();
      const isoDate = now.toISOString().slice(0, 10);
      const level = (framework.ens_level || "basico").toUpperCase();
      const score = framework.summary?.score ?? 0;
      const total = framework.summary?.total_controls ?? 0;
      preamble.push(`# CIBRAXIS · Registro de cumplimiento ENS (RD 311/2022)`);
      preamble.push(`# Cliente: ${framework.client_name}`);
      preamble.push(`# Categoría del sistema (Anexo I RD 311/2022): ${level}`);
      preamble.push(`# Score ponderado por nivel: ${score}/100 · ${total} controles aplicables`);
      preamble.push(`# Ponderación aplicada: BÁSICO ×1 · MEDIO ×2 · ALTO ×3 (Anexo II RD 311/2022)`);
      preamble.push(`# Emitido: ${isoDate}`);
      preamble.push(``);
    }
    const body = [...preamble, header, ...rows].join("\n");
    const blob = new Blob([body], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const dateSuffix = isEns ? `-${new Date().toISOString().slice(0, 10)}` : "";
    a.download = `CIBRAXIS-${framework.framework.short.replace(/\s+/g, "-")}-${framework.client_name.replace(/\s+/g, "_")}${dateSuffix}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("Registro de cumplimiento exportado");
  };
  return (
    <button
      onClick={downloadCsv}
      className="font-mono text-[10px] tracking-widest uppercase border border-border px-3 py-2 hover:border-primary hover:text-primary"
      data-testid="compliance-export-csv"
    >
      Exportar CSV
    </button>
  );
}

const csv = (v) => `"${(v || "").replace(/"/g, '""')}"`;
